import { BusinessObjectBase } from './base';
import { measurementSetSchema } from '@schemas/business-objects/measurement-set';
import type { Measurement } from './measurement';
import type { SalePhoto } from './sale-photo';
import type { User } from './user';
import type { Id } from '../base-types';

export interface MeasurementSetData {
  readonly id: Id;
  readonly createdOn?: Date;
  readonly referenceLength?: number;
  readonly scaleFactor?: number;
  readonly salePhotoId: Id;
  readonly salePhoto?: SalePhoto;
  readonly createdById: Id;
  readonly createdBy?: User;
  readonly measurements?: Measurement[];
}

export interface MeasurementSet extends Readonly<MeasurementSetData> {}
export class MeasurementSet extends BusinessObjectBase {
  static readonly entityName = 'MeasurementSet' as const;
  static readonly plural = 'measurement-sets';
  static schema = measurementSetSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<MeasurementSet[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new MeasurementSet(data));
  }

  static async findOne(filter?: any): Promise<MeasurementSet | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new MeasurementSet(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<MeasurementSet> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new MeasurementSet(data);
  }

  static async create(
    data: Omit<MeasurementSetData, 'id'>,
  ): Promise<MeasurementSet> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new MeasurementSet(result);
  }

  static async createMany(
    data: Omit<MeasurementSetData, 'id'>[],
  ): Promise<MeasurementSet[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new MeasurementSet(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<MeasurementSetData>,
  ): Promise<MeasurementSet> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new MeasurementSet(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
